/**
 * Client-only, anonymous state backing the "first use" and "variant breadth"
 * analytics events. Everything lives in localStorage and ONLY aggregate
 * numbers/flags are ever sent to Umami — never an identity, a list, or a user
 * id — which keeps the analytics posture cookie-free and anonymous
 * (see docs/GDPR.md). All access is wrapped so storage being unavailable
 * (private mode, SSR/prerender) never throws into the app.
 */

const FIRST_PREFIX = 'draughts-analytics-first:';
const VARIANTS_PLAYED_KEY = 'draughts-analytics-variants-played';

/**
 * Returns true the FIRST time it is called for `key` (and records it), false on
 * every later call. On storage failure returns false — we would rather miss a
 * "first use" event than emit a spurious one.
 */
export function markFirst(key: string): boolean {
  try {
    const storageKey = FIRST_PREFIX + key;
    if (localStorage.getItem(storageKey)) return false;
    localStorage.setItem(storageKey, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Record that this browser has played `variantCode` and return the running
 * count of distinct variants ever played here plus whether this one is new.
 * This is the per-browser PROXY for "how many variants does a user play" — the
 * exact, per-user answer comes from the server-side SQL (see docs/ANALYTICS.md).
 */
export function recordVariantPlayed(variantCode: string): { distinctCount: number; isNew: boolean } {
  try {
    const raw = localStorage.getItem(VARIANTS_PLAYED_KEY);
    const played = new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
    const isNew = !played.has(variantCode);
    if (isNew) {
      played.add(variantCode);
      localStorage.setItem(VARIANTS_PLAYED_KEY, JSON.stringify([...played]));
    }
    return { distinctCount: played.size, isNew };
  } catch {
    return { distinctCount: 0, isNew: false };
  }
}
